/**
 * Product manager — handles product model loading and catalog fetching.
 *
 * Stateless extraction from ForgeTryonElement so the widget class stays
 * focused on orchestration. All methods accept a context object with the
 * dependencies they need, keeping coupling explicit.
 */

import { RestDataProvider } from '@mirrably/core';
import type { MediaPort } from '@mirrably/core';
import { showStateScreen, updateProductInfo, CatalogSlider } from '@mirrably/core/ui';
import type { ModalElements } from '@mirrably/core/ui';
import type { ForgeTryonConfig } from './config.js';
import type { TryonScene } from '../renderer/scene.js';
import type { GlassesManager } from '../renderer/glasses.js';
import type { Analytics } from '@mirrably/core';
import type { EventBus } from '@mirrably/core';

/** Dependencies needed for product/catalog operations. */
export interface ProductManagerContext {
  config: ForgeTryonConfig;
  modal: ModalElements | null;
  catalog: CatalogSlider | null;
  scene: TryonScene | null;
  glasses: GlassesManager | null;
  analytics: Analytics;
  bus: EventBus;
  mediaPort: MediaPort | null;
  /** Callback to load a product model (re-enters widget.loadProduct). */
  loadProduct: (productId: string) => Promise<void>;
  /** Returns false when a newer product request has superseded this one. */
  isLatestProductRequest?: (productId: string) => boolean;
}

/**
 * Fetch a product from the API and load its 3-D model.
 *
 * Non-fatal: on failure the camera feed continues without glasses overlay.
 */
export async function loadProduct(
  ctx: ProductManagerContext,
  productId: string,
): Promise<void> {
  if (!ctx.scene || !ctx.glasses) return;
  if (!ctx.config.apiUrl) return;

  // Show loading indicator on the product info bar
  setModelLoading(ctx, true);

  const loadStart = Date.now();
  try {
    const provider = new RestDataProvider({
      baseUrl: ctx.config.apiUrl,
      ...(ctx.config.apiKey ? { apiKey: ctx.config.apiKey } : {}),
      ...(ctx.mediaPort ? { mediaPort: ctx.mediaPort } : {}),
    });
    const product = await provider.getProduct(productId);
    if (ctx.isLatestProductRequest && !ctx.isLatestProductRequest(productId)) {
      return;
    }
    // Resolve relative model URLs against the API base URL.
    // The API returns paths like "/v1/assets/..." which the GLTFLoader would
    // otherwise resolve against the page origin (wrong host in dev).
    let modelUrl = product.modelUrl;
    if (modelUrl.startsWith('/') && ctx.config.apiUrl) {
      const apiOrigin = new URL(ctx.config.apiUrl).origin;
      modelUrl = apiOrigin + modelUrl;
    }
    if (ctx.config.debug) console.log('[forge-tryon] Loading model:', modelUrl);
    await ctx.glasses.swapByUrl(product.id, modelUrl, ctx.scene);
    if (ctx.isLatestProductRequest && !ctx.isLatestProductRequest(productId)) {
      return;
    }
    // Apply per-product position/scale overrides after model is loaded
    if (product.positionOffset || product.scale !== undefined) {
      ctx.scene.applyPositionOverrides(
        product.positionOffset ?? [0, 0, 0],
        product.scale ?? 1.0,
      );
    }
    if (ctx.config.debug) console.log('[forge-tryon] Model loaded successfully:', productId);
    ctx.analytics.trackModelLoaded(productId, Date.now() - loadStart);
  } catch (err) {
    // Non-fatal: model load failure shows camera feed without overlay
    console.error('[forge-tryon] Model load failed:', err);
    ctx.bus.emit('error', err as Error);
    const errMsg = err instanceof Error ? err.message : String(err);
    ctx.analytics.trackError(errMsg, 'model_load');
  } finally {
    setModelLoading(ctx, false);
  }
}

/**
 * Fetch the full product catalog and populate the CatalogSlider.
 *
 * Non-fatal: catalog failure does not block the camera/AR experience.
 */
export async function loadCatalog(ctx: ProductManagerContext): Promise<void> {
  if (!ctx.catalog || !ctx.config.storeId || !ctx.config.apiUrl) return;

  try {
    const provider = new RestDataProvider({
      baseUrl: ctx.config.apiUrl,
      ...(ctx.config.apiKey ? { apiKey: ctx.config.apiKey } : {}),
      ...(ctx.mediaPort ? { mediaPort: ctx.mediaPort } : {}),
    });
    const products = await provider.getCatalog(ctx.config.storeId);
    if (!ctx.catalog) return; // modal may have closed while fetching
    ctx.catalog.setProducts(products);

    // Highlight the initial product and show its name in the info bar.
    // If the specified product-id doesn't exist in the catalog, fall back
    // to the first product so the user sees glasses rather than nothing.
    let targetId = ctx.config.productId;
    if (targetId) {
      const match = products.find((p) => p.id === targetId);
      if (!match && products.length > 0) {
        targetId = products[0].id;
      }
    } else if (products.length > 0) {
      targetId = products[0].id;
    }

    if (targetId) {
      ctx.catalog.selectProduct(targetId);
      const match = products.find((p) => p.id === targetId);
      if (match && ctx.modal) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const price = (match as any).price as string | undefined;
        updateProductInfo(ctx.modal.productInfoBar, match.name ?? match.id, price);
      }
      // Auto-load the model for the selected product
      if (targetId !== ctx.config.productId) {
        void ctx.loadProduct(targetId);
      }
    }
  } catch (err) {
    // Catalog fetch failed — typically CORS, network, or 5xx from the API.
    // Show a branded state screen with a Refresh action so the user sees a
    // clear recovery path instead of an empty sidebar (LH6).
    const errMsg = err instanceof Error ? err.message : String(err);
    console.error('[forge-tryon] Catalog load failed:', err);
    ctx.bus.emit('error', err as Error);
    ctx.analytics.trackError(errMsg, 'catalog_load');
    if (ctx.modal) {
      showStateScreen(
        ctx.modal.stateScreen,
        'Try-on temporarily unavailable',
        "We couldn't reach the product catalog. Please refresh the page and try again.",
        'ft-',
        'Refresh',
        () => {
          if (typeof window !== 'undefined' && window.location) {
            window.location.reload();
          }
        },
      );
    }
  }
}

/** Toggle a subtle loading indicator on the product info bar. */
export function setModelLoading(ctx: ProductManagerContext, loading: boolean): void {
  if (!ctx.modal) return;
  const info = ctx.modal.container.querySelector('.ft-product-info');
  if (!info) return;
  if (loading) {
    info.classList.add('ft-product-info--loading');
  } else {
    info.classList.remove('ft-product-info--loading');
  }
}
